import enrolLoyaltyAccount from 'actions/loyalty/enrolLoyaltyAccount'
import { dismissLoyaltyMembershipActivationBanner, isLoyaltyMembershipActivationBannerDismissed } from 'actions/loyalty/membershipActivationBannerStorage'
import LoyaltyTierLabel from 'components/Common/Labels/LoyaltyTierLabel'
import LayoutContainer from 'components/Common/LayoutContainer'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Colour from 'components/utils/Colour'
import Group from 'components/utils/Group'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useLoyaltyUserEligibilityTier from 'loyalty/hooks/useLoyaltyUserEligibilityTier'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import { rem } from 'polished'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import LoyaltyProductLogo from '../LoyaltyProductLogo'
import IconButton from 'LuxKit/Button/IconButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import { useIntl } from 'react-intl'

const Container = styled.div`
  padding-block: ${rem(12)};

  &.tier-bronze {
    background: linear-gradient(to right, ${props => props.theme.palette.product.loyalty.bronze.normalBackground}, ${props => props.theme.palette.product.loyalty.bronze.lightBackground});
    color: ${props => props.theme.palette.product.loyalty.bronze.normalContrast};
  }
  &.tier-silver {
    background: linear-gradient(to right, ${props => props.theme.palette.product.loyalty.silver.normalBackground}, ${props => props.theme.palette.product.loyalty.silver.lightBackground});
    color: ${props => props.theme.palette.product.loyalty.silver.normalContrast};
  }
  &.tier-gold {
    background: linear-gradient(to right, ${props => props.theme.palette.product.loyalty.gold.normalBackground}, ${props => props.theme.palette.product.loyalty.gold.lightBackground});
    color: ${props => props.theme.palette.product.loyalty.gold.normalContrast};
  }
  &.tier-platinum {
    background: linear-gradient(to right, ${props => props.theme.palette.product.loyalty.platinum.normalBackground}, ${props => props.theme.palette.product.loyalty.platinum.lightBackground});
    color: ${props => props.theme.palette.product.loyalty.platinum.normalContrast};
  }
`

const StyledLayoutContainer = styled(LayoutContainer)`
  display: grid;
  grid-template:
    "content . dismiss" auto / auto 1fr min-content;

  ${mediaQueryUp.tablet} {
    grid-template:
      ". content . dismiss" auto / 1fr auto 1fr min-content;
    padding-right: ${rem(20)};
    padding-left: ${rem(20)};
    align-items: center;
  }
`

const ContentWrapper = styled(Group)`
  grid-area: content;
`

// Same spacings from src/LuxKit/Banners/PageBanner.tsx
const DismissButton = styled(IconButton)`
  grid-area: dismiss;
  margin: ${rem(-4)} ${rem(-4)} 0 ${rem(8)};

  ${mediaQueryUp.tablet} {
    margin: 0 0 0 ${rem(24)};
  }
`

function LoyaltyMembershipActivationPageBanner() {
  const [accountEligibleTier, accountEligibleTierLoading, accountEligibleTierError] = useLoyaltyUserEligibilityTier()
  const [isDismissed, setIsDismissed] = useState(isLoyaltyMembershipActivationBannerDismissed())
  const givenName = useAppSelector(state => state.auth.account.givenName)
  const intl = useIntl()

  const dispatch = useAppDispatch()
  const activateMembership = useCallback(() => {
    dispatch(enrolLoyaltyAccount({ enrolment: 'existingUserRegistration' }))
  }, [dispatch])

  const onDismiss = useCallback(() => {
    dismissLoyaltyMembershipActivationBanner()
    setIsDismissed(true)
  }, [])

  const isDesktop = useScreenSizeUp('desktop')
  const tierName = accountEligibleTier ? getLoyaltyTierDisplayName(accountEligibleTier) : undefined

  const exclusiveInviteText = intl.formatMessage({
    id: 'UFRPGu89LJqf5bfH3uc6',
    defaultMessage: 'Exclusive Invite',
    description: 'Label for exclusive invite in loyalty membership activation banner',
  })

  const activationMessage = useMemo(() => {
    if (!accountEligibleTier || !tierName) {
      return ''
    }

    if (givenName) {
      return intl.formatMessage(
        {
          id: 'C5Ucbn3CDNKO-bhc0QQE',
          defaultMessage: '{name}, you’ve unlocked <tierStatus>{tierName} status</tierStatus> in our new rewards program. <activateLink>Activate for free</activateLink>',
          description: 'Loyalty membership activation banner message with user name, tier status, and activation link',
        },
        {
          name: givenName,
          tierName,
          tierStatus: (chunks: React.ReactNode) => (
            <Colour key="tier-status" value={`tier-${accountEligibleTier}-light-contrast`} as="b">
              {chunks}
            </Colour>
          ),
          activateLink: (chunks: React.ReactNode) => (
            <TextLink key="activate-link" size="medium" onClick={activateMembership}>
              {chunks}
            </TextLink>
          ),
        },
      )
    }

    return intl.formatMessage(
      {
        id: 'aV4fBntCdUzgqkIYtOxZ',
        defaultMessage: 'You’ve unlocked <tierStatus>{tierName} status</tierStatus> in our new rewards program. <activateLink>Activate for free</activateLink>',
        description: 'Loyalty membership activation banner message with tier status and activation link',
      },
      {
        tierName,
        tierStatus: (chunks: React.ReactNode) => (
          <Colour key="tier-status" value={`tier-${accountEligibleTier}-light-contrast`} as="b">
            {chunks}
          </Colour>
        ),
        activateLink: (chunks: React.ReactNode) => (
          <TextLink key="activate-link" size="medium" onClick={activateMembership}>
            {chunks}
          </TextLink>
        ),
      },
    )
  }, [intl, givenName, tierName, accountEligibleTier, activateMembership])

  if (isDismissed || !isDesktop || !accountEligibleTier || accountEligibleTierLoading || accountEligibleTierError || accountEligibleTier === 'platinum') return null

  return <Container className={`tier-${accountEligibleTier}`}>
    <StyledLayoutContainer>
      <ContentWrapper direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
        <Group direction="vertical" tabletDirection="horizontal" tabletHorizontalAlign="center" gap="8 20" verticalAlign="center" wrap="wrap">
          <Group direction="horizontal" gap={12} verticalAlign="center">
            <LoyaltyProductLogo height={24} />
            <LoyaltyTierLabel kind="primary" tier={accountEligibleTier} format="uppercase">{exclusiveInviteText}</LoyaltyTierLabel>
          </Group>
          <BodyText variant="medium">
            {activationMessage}
          </BodyText>
        </Group>
      </ContentWrapper>
      <DismissButton
        kind="tertiary"
        size="small"
        onClick={onDismiss}
      >
        <LineTimesIcon />
      </DismissButton>
    </StyledLayoutContainer>
  </Container>
}

export default LoyaltyMembershipActivationPageBanner
